/**
 * Display names for the orgs in the switcher. Only real names come back: an org whose name could not be read is
 * listed in `failed`, not named after its id - the switcher used to show raw ids whenever the lookup failed (a deploy
 * restarting the server, a token mid-refresh), and kept them until the next hourly token rotation. The caller merges
 * what came back into the names it already has, so one failed lookup never wipes a name that was shown before, and
 * asks again for the failed ones.
 *
 * getOrgById needs the ORG page at READ_ONLY, which a plain member does not hold; the name alone is readable by any
 * member, so that is asked for before giving up.
 *
 * @returns {Promise<{ names: Object<string,string>, dataMap: Object<string,Object>, failed: string[] }>}
 */
export const fetchOrgNames = async (orgs, { getOrgById, getOrgNameById, accessToken }) => {
    const names = {};
    const dataMap = {};
    const failed = [];
    for (const org of orgs) {
        try {
            const orgData = await getOrgById({ id: org.orgId, accessToken });
            if (orgData?.org?.orgName) names[org.orgId] = orgData.org.orgName;
            dataMap[org.orgId] = orgDataOf(orgData?.org);
        } catch {
            try {
                const named = await getOrgNameById({ id: org.orgId, accessToken });
                if (named?.orgName) names[org.orgId] = named.orgName;
            } catch {
                failed.push(org.orgId);
            }
        }
    }
    return { names, dataMap, failed };
};

// allowedDomains may come as flat ['d1','d2'], nested [['d1','d2']], or comma-joined ['d1,d2'] (from incorrect DB
// edits) — handle all cases.
export const flattenDomains = (rawDomains) => (rawDomains || []).flat().flatMap((d) => (
    typeof d === 'string' && d.includes(',') ? d.split(',').map((s) => s.trim()).filter(Boolean) : [d]
));

/** The org fields the header keeps per org: owners (management UI), domains, and state (the switcher's pill). */
export const orgDataOf = (org) => ({
    orgOwner: org?.orgOwner || null,
    coOwners: org?.coOwners || [],
    allowedDomains: flattenDomains(org?.allowedDomains),
    orgState: org?.orgState || null,
});

// Failed names are asked for again after these waits, then every two minutes.
export const ORG_NAME_RETRY_DELAYS_MS = [5000, 15000, 45000, 120000];

export const orgNameRetryDelay = (attempt) => ORG_NAME_RETRY_DELAYS_MS[
    Math.min(attempt, ORG_NAME_RETRY_DELAYS_MS.length - 1)
];
